import 'firebase/compat/auth'
import 'firebase/compat/firestore'

import firebaseRaw from 'firebase/compat/app'

import {GetUserParams} from '../api/user/get-user'
import {logger} from '../monitoring/logger'
// import {API} from '../networking/http'
import {store} from '../redux/store'
import {AuthService} from '../services/auth'
import {getUpdatedLocalizations} from '../services/global/actions'
import {UserService} from '../services/user'
import {firebaseOptions} from './environment'
import {removeAuthToken, setAuthToken} from './local-storage'
import {queryClient} from './react-query'

const {APP_ID, API_KEY, PROJECT_ID, AUTH_DOMAIN, DATABASE_URL, STORAGE_BUCKET, MEASUREMENT_ID, MESSAGING_SENDER_ID} =
  firebaseOptions

const firebaseConfig = {
  appId: APP_ID,
  apiKey: API_KEY,
  projectId: PROJECT_ID,
  authDomain: AUTH_DOMAIN,
  databaseURL: DATABASE_URL,
  storageBucket: STORAGE_BUCKET,
  measurementId: MEASUREMENT_ID,
  messagingSenderId: MESSAGING_SENDER_ID,
}

firebaseRaw.initializeApp(firebaseConfig)

export const auth = firebaseRaw.auth()
export const db = firebaseRaw.firestore()

const provider = new firebaseRaw.auth.GoogleAuthProvider()
type AccountExistsError = firebaseRaw.auth.Error & {
  email?: string
  credential?: firebaseRaw.auth.AuthCredential
}

let passwordRequestHandler: ((email: string) => Promise<string | null>) | null = null
export const setPasswordRequestHandler = (handler: (email: string) => Promise<string | null>) => {
  passwordRequestHandler = handler
}

export let allowPhoneOnlyLogin = false
export const setAllowPhoneOnlyLogin = (value: boolean) => {
  allowPhoneOnlyLogin = value
}
export const createUserDTOHelper = (user: firebaseRaw.User | null) => {
  const name = user?.displayName || ''
  const [firstName = '', lastName = ''] = name.split(' ')
  const venueId = localStorage.getItem('lastVisitedVenueId')
  // TODO: IMPORTANT user?.emailVerified, 'if this field is true, then user login provider will be added, in other cases google provider override email & password')
  const dataToSave = {
    venueId: venueId || '',
    dob: null,
    gender: null,
    access: true,
    role: ['USER'],
    verified: false,
    withPassword: false,
    userAuthId: user?.uid,
    firstName: firstName || '',
    lastName: lastName || '',
    phone: user?.phoneNumber || null,
    providerData: user?.providerData || [],
    uid: user?.uid,
    email: user?.email || '',
    name: user?.displayName || null,
    avatar: user?.photoURL || null,
  }
  return dataToSave
}
export const signInWithGoogle = async () => {
  try {
    const result = await firebaseRaw.auth().signInWithPopup(provider)
    const isNewUser = result.additionalUserInfo?.isNewUser
    const user = result.user
    const dataToSave = createUserDTOHelper(result.user)

    // TODO: move this logic to handler getUserQuery
    // if (isNewUser) {
    //   // create new user in firebase
    //   await API.post<unknown, unknown>('user/socials', dataToSave)
    // }

    if (!isNewUser) {
      store.dispatch(
        AuthService.actions.signInRequestAction({
          email: dataToSave.email,
          venueId: dataToSave.venueId,
          isSocial: true,
        }),
      )
    }
    return user
  } catch (error) {
    const err = error as firebaseRaw.auth.Error
    // Handle account linking when the email already exists with another provider (e.g., password)
    if (err.code === 'auth/account-exists-with-different-credential') {
      try {
        const accountErr = err as AccountExistsError
        const email = accountErr.email
        const pendingCred = accountErr.credential
        if (!email || !pendingCred) {
          console.error('Cannot resolve linking flow: missing email or pending credential')
          return null
        }
        const methods = await auth.fetchSignInMethodsForEmail(email)
        // Require password method; otherwise, inform and exit
        const passwordMethod = firebaseRaw.auth.EmailAuthProvider.EMAIL_PASSWORD_SIGN_IN_METHOD
        if (!methods.includes(passwordMethod)) {
          console.error('Existing sign-in methods for email:', methods)
          return null
        }
        const password = passwordRequestHandler ? await passwordRequestHandler(email) : null
        if (!password) {
          console.warn('Linking cancelled by user: password not provided')
          return null
        }
        const userCred = await auth.signInWithEmailAndPassword(email, password)
        await userCred.user?.linkWithCredential(pendingCred)
        const linkedUser = auth.currentUser
        const dataToSave = createUserDTOHelper(linkedUser)
        store.dispatch(
          AuthService.actions.signInRequestAction({
            email: dataToSave.email,
            venueId: dataToSave.venueId,
            isSocial: true,
          }),
        )
        return linkedUser
      } catch (linkErr) {
        console.error('Failed to complete account linking flow:', linkErr)
        return null
      }
    }
    console.error('Login error:', err)
    return null
  }
}

export const handlePasswordLogin = async (email: string, password: string) => {
  const passwordMethod = firebaseRaw.auth.EmailAuthProvider.EMAIL_PASSWORD_SIGN_IN_METHOD
  const googleMethod = 'google.com'
  const methods = await auth.fetchSignInMethodsForEmail(email)
  // Existing password: normal sign-in
  if (methods.includes(passwordMethod)) {
    const credential = await auth.signInWithEmailAndPassword(email, password)
    // If Google is also an available method but not yet linked on the current user, link it now
    const providerData = credential.user ? credential.user.providerData : []
    const hasGoogleProvider = providerData.some((p) => p && p.providerId === googleMethod)
    if (methods.includes(googleMethod) && !hasGoogleProvider) {
      try {
        await credential.user?.linkWithPopup(new firebaseRaw.auth.GoogleAuthProvider())
      } catch (linkErr) {
        // If Google credential already in use by another account, we skip linking to avoid breaking session
        // Callers may offer a manual merge UX later if needed
        // eslint-disable-next-line no-console
        console.warn('Google already linked elsewhere or linking cancelled:', (linkErr as Error).message)
      }
    }
    return auth.currentUser
  }
  // Existing Google but no password yet: do NOT create a new user
  if (methods.includes(googleMethod)) {
    // Let caller decide UX (e.g., prompt to sign in with Google first, or initiate linking)
    return null
  }
  // No existing providers: safe to create a new account
  const created = await auth.createUserWithEmailAndPassword(email, password)
  return created.user
}

auth.onAuthStateChanged(async (user) => {
  store.dispatch(getUpdatedLocalizations())
  if (!user) {
    store.dispatch(AuthService.actions.isLoggedIn(false))
    store.dispatch(AuthService.actions.isInitialized(true))
    queryClient.resetQueries({queryKey: ['GetUserQuery']})
    removeAuthToken()
    return
  }

  console.log('onAuthStateChanged user:', {
    uid: user.uid,
    email: user.email,
    providerData: user.providerData,
    isPhoneOnlyUser:
      user.providerData.length === 1 && user.providerData[0]?.providerId === 'phone' && !user.email,
  })

  // Skip auto-login if user only has phone provider and no email
  // This means they are mid phone-login flow (needs_email step)
  const isPhoneOnlyUser =
    user.providerData.length === 1 && user.providerData[0]?.providerId === 'phone' && !user.email

  if (isPhoneOnlyUser && !allowPhoneOnlyLogin) {
    // Don't set authToken or isLoggedIn — wait for phone-login flow to complete
    store.dispatch(AuthService.actions.isInitialized(true))
    return
  }

  // Reset the flag after use
  allowPhoneOnlyLogin = false
  const tokenData = await user.getIdTokenResult(true)
  setAuthToken(tokenData)
  store.dispatch(AuthService.actions.isLoggedIn(true))
  store.dispatch(AuthService.actions.isInitialized(true))

  try {
    const params: GetUserParams = {id: user.uid}
    const userFromBack = await queryClient.fetchQuery(UserService.queries.getUserQuery({params, query: undefined}))
    logger.log(userFromBack)
  } catch (e) {
    console.error('getUserQuery failed in onAuthStateChanged:', e)
  }
})

auth.onIdTokenChanged(async (user) => {
  if (user) {
    const tokenData = await user.getIdTokenResult()
    setAuthToken(tokenData)
  } else {
    removeAuthToken()
  }
})

export const sendOtp = async (phoneNumber: string) => {
  try {
    const globalWindow = window as Window & typeof globalThis & { recaptchaVerifier?: firebaseRaw.auth.RecaptchaVerifier | null }
    // 1. Destroy any existing verifier instance
    if (globalWindow.recaptchaVerifier) {
      try {
        globalWindow.recaptchaVerifier.clear()
      } catch (e) {
        // ignore error
      }
      globalWindow.recaptchaVerifier = null
    }

    // 2. Destroy any existing DOM container
    const oldContainer = document.getElementById('recaptcha-global')
    if (oldContainer) {
      oldContainer.remove()
    }

    // 3. Create a brand new DOM container
    const container = document.createElement('div')
    container.id = 'recaptcha-global'
    document.body.appendChild(container)

    // 4. Create a fresh verifier instance
    const verifier = new firebaseRaw.auth.RecaptchaVerifier('recaptcha-global', {
      size: 'invisible',
    })
    globalWindow.recaptchaVerifier = verifier

    // 5. Send OTP
    return await auth.signInWithPhoneNumber(phoneNumber, verifier as firebaseRaw.auth.ApplicationVerifier)
  } catch (e: unknown) {
    const globalWindow = window as Window & typeof globalThis & { recaptchaVerifier?: firebaseRaw.auth.RecaptchaVerifier | null }
    // Clean up on failure so the next attempt is fresh
    if (globalWindow.recaptchaVerifier) {
      try {
        globalWindow.recaptchaVerifier.clear()
      } catch (err) {
        // ignore error
      }
      globalWindow.recaptchaVerifier = null
    }
    const oldContainer = document.getElementById('recaptcha-global')
    if (oldContainer) {
      oldContainer.remove()
    }
    throw e
  }
}

export const verifyOtp = async (confirmationResult: firebaseRaw.auth.ConfirmationResult, otp: string) => {
  const result = await confirmationResult.confirm(otp)
  return await result.user?.getIdToken()
}

export const firebase = firebaseRaw
